// What the call review edits, and the document that is saved (user, 7 Oct 2026: "save everything including boxes
// coords and labels for model training"). Coordinates are in seconds (file time) and Hz; `frame` maps them to the
// spectrogram image (x = (t - x0) / (x1 - x0), y = 1 - f / fmax, image 800 x 320 px), and every box also carries
// `norm` = the same box as 0-1 image coordinates, ready for an object detector. Engine items the person deleted are kept
// under `removed` (what the engine got wrong is training data too). Origins: engine | adjusted | added.
import { sectionForSpecies, speciesName } from './sections';
import { amax, amin } from './safeMath';

export const SCHEMA = 'cetalingua.callReview/1';
export const IMG_W = 800;
export const IMG_H = 320;

export const DISMISS_REASONS = [
  ['faint', 'Too faint to measure'],
  ['unclear', 'Unclear / several sounds overlap'],
  ['noise_equipment', 'Not an animal — equipment / mechanical noise (hum, recorder, hard drive, electrical)'],
  ['noise_boat', 'Not an animal — boat / engine / sonar'],
  ['noise_shrimp', 'Not an animal — snapping shrimp'],
  ['noise', 'Not an animal — other noise (rain, waves, flow, handling)'],
  ['other_animal', 'A different animal or sound type (say which in the note)'],
  ['cutoff', 'Cut off — the call is incomplete'],
  ['duplicate', 'Same call as another item'],
  ['other', 'Other reason'],
];

let seq = 0;
export const uid = (p) => `${p}${Date.now().toString(36)}${(seq++).toString(36)}`;
const r1 = (x) => Math.round(x * 10) / 10;
const r3 = (x) => Math.round(x * 1000) / 1000;

// engine pitch list [[t, Hz], ...] split where it breaks (gap > 0.05 s or a jump > 25 %): one F0 line per piece
export function splitPitch(pts) {
  const out = []; let cur = [];
  (pts || []).forEach((p, i) => {
    const q = pts[i - 1];
    if (q && (p[0] - q[0] > 0.05 || Math.abs(p[1] - q[1]) / q[1] > 0.25)) { if (cur.length > 1) out.push(cur); cur = []; }
    cur.push(p);
  });
  if (cur.length > 1) out.push(cur);
  return out;
}

// fewer points for editing (every point is a dot the person can drag); keeps the ends
export function thin(points, max = 40) {
  if (points.length <= max) return points.map((p) => [...p]);
  const step = (points.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => [...points[Math.round(i * step)]]);
}

export function fromEngine(entry, section) {
  const lines = [];
  if (entry.lines) {
    entry.lines.forEach((l) => lines.push({ id: uid('l'), kind: l.kind, k: l.k || 1, points: thin(l.points), origin: 'engine' }));
  } else if (entry.f0) {
    splitPitch(entry.f0).forEach((seg) => lines.push({ id: uid('l'), kind: 'f0', k: 1, points: thin(seg), origin: 'engine' }));
  }
  if (section.autoHarmonics && !lines.some((l) => l.kind === 'harmonic')) {
    // humpback: harmonics follow the F0 line (k x F0) until the person edits one
    const f0s = lines.filter((l) => l.kind === 'f0').flatMap((l) => l.points.map((p) => p[1]));
    const med = f0s.length ? [...f0s].sort((a, b) => a - b)[Math.floor(f0s.length / 2)] : 0;
    const top = med ? Math.min(8, Math.floor((entry.fmax || 4000) / med)) : 0;
    for (let k = 2; k <= top; k += 1) lines.push({ id: uid('h'), kind: 'harmonic', k, follows: 'f0', points: [], origin: 'engine' });
  }
  return {
    call: { ...entry.call, origin: 'engine' },
    lines,
    boxes: (entry.boxes || []).map((b) => ({ id: uid('b'), ...b, origin: b.origin || 'engine' })),
    marks: (entry.marks || []).map((m) => ({ id: uid('m'), t: m[0], f: m[1], origin: 'engine' })),
    parts: (entry.parts || []).map((p) => ({ id: uid('p'), k: p.k, a: p.a, b: p.b, origin: 'engine' })),
    removed: { lines: [], boxes: [], marks: [], parts: [] },
    edits: {},
    label: { broad: entry.broad || '', code: entry.code || '', speciesId: entry.speciesId || null, speciesLabel: entry.species || null },
    status: 'open',
    openedAt: Date.now(),
  };
}

// points of a line as drawn: a harmonic that follows F0 is k x every F0 line
export function drawnLines(state) {
  const f0 = state.lines.filter((l) => l.kind === 'f0');
  return state.lines.flatMap((l) => (l.follows === 'f0'
    ? f0.map((f, i) => ({ ...l, seg: i, points: f.points.map(([t, v]) => [t, v * l.k]) }))
    : [{ ...l, seg: 0 }]));
}

export function markAdjusted(o) { if (o.origin === 'engine') o.origin = 'adjusted'; }

// remove an item; engine items go to `removed` with their engine values (training: what the engine got wrong)
export function removeItem(state, group, id) {
  const i = state[group].findIndex((x) => x.id === id);
  if (i < 0) return;
  const [it] = state[group].splice(i, 1);
  if (it.origin !== 'added') state.removed[group].push(it);
}
export function restoreAll(state) {
  ['lines', 'boxes', 'marks', 'parts'].forEach((g) => { state[g].push(...state.removed[g]); state.removed[g] = []; });
}

// insert a point keeping the line ordered in time (so a click before the start or after the end extends the line)
export function addPoint(line, t, f) {
  const p = [r3(t), r1(f)];
  const i = line.points.findIndex((q) => q[0] > p[0]);
  if (i < 0) line.points.push(p); else line.points.splice(i, 0, p);
  return i < 0 ? line.points.length - 1 : i;
}

// measurements that follow the drawing: F0 line -> start / end / min / max / median / range; call box -> duration etc.
export function derived(section, state) {
  const out = {};
  // only once the person changed them: an untouched engine line or box keeps the engine's own measurements
  const f0Changed = state.lines.some((l) => l.kind === 'f0' && l.origin !== 'engine') || state.removed.lines.some((l) => l.kind === 'f0');
  const f0 = state.lines.filter((l) => l.kind === 'f0').flatMap((l) => l.points).sort((a, b) => a[0] - b[0]);
  if (section.f0 && f0Changed && f0.length) {
    const v = f0.map((p) => p[1]); const s = [...v].sort((a, b) => a - b);
    const k = section.f0;
    if (k.start) out[k.start] = r1(v[0]);
    if (k.end) out[k.end] = r1(v[v.length - 1]);
    if (k.min) out[k.min] = r1(s[0]);
    if (k.max) out[k.max] = r1(s[s.length - 1]);
    if (k.median) out[k.median] = r1(s[Math.floor(s.length / 2)]);
    if (k.range) out[k.range] = r1(s[s.length - 1] - s[0]);
  }
  if (section.call && state.call && state.call.origin !== 'engine') {
    const c = state.call; const k = section.call;
    if (k.duration) out[k.duration] = r3(c.t1 - c.t0);
    if (k.low) out[k.low] = Math.round(c.f0);
    if (k.high) out[k.high] = Math.round(c.f1);
    if (k.bandwidth) out[k.bandwidth] = Math.round(c.f1 - c.f0);
  }
  return out;
}

// whole-call box around every drawn line (F0, harmonics, subharmonics) with a small margin
export function fitCallToLines(state, fmax) {
  const pts = drawnLines(state).flatMap((l) => l.points);
  if (!pts.length) return false;
  const ts = pts.map((p) => p[0]); const fs = pts.map((p) => p[1]);
  const t0 = amin(ts); const t1 = amax(ts); const f0 = amin(fs); const f1 = amax(fs);
  const pt = Math.max(0.02, (t1 - t0) * 0.03); const pf = Math.max(10, (f1 - f0) * 0.08);
  Object.assign(state.call, { t0: r3(t0 - pt), t1: r3(t1 + pt), f0: Math.max(0, Math.round(f0 - pf)), f1: Math.min(fmax, Math.round(f1 + pf)) });
  markAdjusted(state.call);
  return true;
}

// what changed compared with the engine (quick filters for training sets)
function changes(entry, state, measures) {
  const by = (g) => state[g].reduce((a, x) => ({ ...a, [x.origin]: (a[x.origin] || 0) + 1 }), {});
  return {
    code: state.label.code !== (entry.code || ''), broad: state.label.broad !== (entry.broad || ''),
    species: !!state.label.speciesId && state.label.speciesId !== (entry.speciesId || null),
    callBox: state.call.origin !== 'engine',
    lines: by('lines'), boxes: by('boxes'), marks: by('marks'), parts: by('parts'),
    removed: Object.fromEntries(Object.entries(state.removed).map(([g, v]) => [g, v.length])),
    measurements: Object.keys(measures.person).length,
  };
}

/** Partial calls (user 13 Oct 2026: "let the engine know it only selected a partial call, not the call in its
 * entirety"): the reviewer's whole-call box against the engine's. partial = the reviewer's box reaches past the
 * engine's by more than max(0.05 s, 10 % of the call) in time, or by more than 10 % of the call's band in frequency.
 * covered = share of the reviewer's call (time) inside the engine's box. beyondPicture = the call reaches past the
 * engine's picture (training pictures must then be drawn again from the sound around the call). */
export function callExtent(engine, final, entry) {
  if (!engine || !final || final.t1 <= final.t0) return null;
  const dur = final.t1 - final.t0; const band = Math.max(1, final.f1 - final.f0);
  const r3 = (x) => Math.round(x * 1000) / 1000; const r1 = (x) => Math.round(x * 10) / 10;
  const startEarlier = Math.max(0, engine.t0 - final.t0); const endLater = Math.max(0, final.t1 - engine.t1);
  const lowerHz = Math.max(0, engine.f0 - final.f0); const higherHz = Math.max(0, final.f1 - engine.f1);
  const tTol = Math.max(0.05, 0.1 * dur); const fTol = 0.1 * band;
  const partialTime = startEarlier > tTol || endLater > tTol;
  const partialFreq = lowerHz > fTol || higherHz > fTol;
  const covered = Math.max(0, Math.min(engine.t1, final.t1) - Math.max(engine.t0, final.t0)) / dur;
  return { partial: partialTime || partialFreq, partialTime, partialFreq, covered: Math.round(covered * 1000) / 1000,
    startEarlier_s: r3(startEarlier), endLater_s: r3(endLater), lowerHz: r1(lowerHz), higherHz: r1(higherHz),
    beyondPicture: !!entry && (final.t0 < entry.x0 - 1e-3 || final.t1 > entry.x1 + 1e-3 || final.f1 > (entry.fmax || Infinity) + 1) };
}

const norm = (b, entry) => {
  const w = entry.x1 - entry.x0; const fm = entry.fmax || 4000;
  const c = (v) => Math.max(0, Math.min(1, Math.round(v * 10000) / 10000));
  return { x0: c((b.t0 - entry.x0) / w), x1: c((b.t1 - entry.x0) / w), y0: c(1 - b.f1 / fm), y1: c(1 - b.f0 / fm) };
};
const clean = ({ id, origin, ...rest }) => ({ id, origin, ...rest });

/** The saved document. meta = {itemId, uid, stationId, fileId, scope, engineVersion, bundleKey}. */
export function toReviewDoc(entry, state, section, meta = {}, decision = { status: 'confirmed' }) {
  const lines = drawnLines(state).filter((l) => l.follows !== 'f0' || l.seg === 0)
    .map((l) => (l.follows === 'f0'
      ? { id: l.id, kind: l.kind, k: l.k, follows: 'f0', origin: l.origin, points: [] }
      : { id: l.id, kind: l.kind, k: l.k, origin: l.origin, points: l.points }));
  const eng = Object.fromEntries(Object.entries(entry.m || {}).filter(([, v]) => v !== null && v !== undefined));
  const fromDrawing = derived(section, state);
  const person = Object.fromEntries(Object.entries(state.edits).filter(([k, v]) => String(eng[k] ?? '') !== String(v)));
  const measures = { engine: eng, person, fromDrawing };
  const signal = section.signal === 'pulsed' ? 'pulsed' : 'tonal';
  const finalSpecies = state.label.speciesId || entry.speciesId || null;
  const moveTo = state.label.speciesId && state.label.speciesId !== (entry.speciesId || null)
    ? sectionForSpecies(state.label.speciesId, signal) : null;
  return {
    schema: SCHEMA,
    itemId: meta.itemId || null, callId: entry.id, section: section.key,
    status: decision.status,                                     // confirmed | dismissed
    dismiss: decision.status === 'dismissed' ? { reason: decision.reason, note: decision.note || '' } : null,
    species: {
      engine: { id: entry.speciesId || null, label: entry.species || null, source: entry.speciesSource || 'engine' },
      final: { id: finalSpecies, label: speciesName(finalSpecies) || state.label.speciesLabel || entry.species || null,
        source: state.label.speciesId && state.label.speciesId !== entry.speciesId ? 'person' : (entry.speciesSource || 'engine') },
      moveTo: moveTo && moveTo !== section.key ? moveTo : null,
    },
    label: { codeSystem: section.codeSystem, code: state.label.code, engineCode: entry.code || null,
      broad: state.label.broad || null, engineBroad: entry.broad || null },
    frame: { img: entry.img, imgWidth: IMG_W, imgHeight: IMG_H, x0: entry.x0, x1: entry.x1, fmin: 0, fmax: entry.fmax || 4000,
      sampleRate: entry.sampleRate || null },
    call: { t0: state.call.t0, t1: state.call.t1, f0: state.call.f0, f1: state.call.f1, origin: state.call.origin, norm: norm(state.call, entry) },
    // the engine's own box, kept even when the reviewer moves it (engine feedback loop, 13 Oct 2026)
    engineCall: entry.call ? { t0: entry.call.t0, t1: entry.call.t1, f0: entry.call.f0, f1: entry.call.f1 } : null,
    extent: decision.status === 'dismissed' ? null : callExtent(entry.call, state.call, entry),
    lines,
    boxes: state.boxes.map((b) => ({ ...clean(b), norm: norm(b, entry) })),
    marks: state.marks.map((m) => ({ t: m.t, f: m.f, origin: m.origin })),
    parts: state.parts.map((p) => ({ k: p.k, a: p.a, b: p.b, origin: p.origin })),
    removed: {
      lines: state.removed.lines.map((l) => ({ kind: l.kind, k: l.k, follows: l.follows || null, points: l.points })),
      boxes: state.removed.boxes.map((b) => ({ kind: b.kind, t0: b.t0, t1: b.t1, f0: b.f0, f1: b.f1 })),
      marks: state.removed.marks.map((m) => ({ t: m.t, f: m.f })),
      parts: state.removed.parts.map((p) => ({ k: p.k, a: p.a, b: p.b })),
    },
    measurements: measures,
    changes: changes(entry, state, measures),
    source: { stationId: meta.stationId || null, fileId: meta.fileId || null, scope: meta.scope || null,
      bundleKey: meta.bundleKey || null, engineVersion: meta.engineVersion || null },
    by: meta.uid || null,
    secondsOpen: Math.round((Date.now() - (state.openedAt || Date.now())) / 1000),
  };
}

/** A saved review back into the editor (approval: the researcher / admin sees the decoder's version and can change it). */
export function fromReviewDoc(entry, section, doc) {
  const s = fromEngine(entry, section);
  const { norm, ...call } = doc.call || {};
  if (doc.call) s.call = call;
  s.lines = (doc.lines || []).map((l) => (l.follows === 'f0'
    ? { id: uid('h'), kind: l.kind, k: l.k, follows: 'f0', points: [], origin: l.origin }
    : { id: uid('l'), kind: l.kind, k: l.k, points: l.points.map((p) => [...p]), origin: l.origin }));
  s.boxes = (doc.boxes || []).map(({ norm: n, id, ...b }) => ({ ...b, id: uid('b') }));
  s.marks = (doc.marks || []).map((m) => ({ id: uid('m'), t: m.t, f: m.f, origin: m.origin }));
  s.parts = (doc.parts || []).map((p) => ({ id: uid('p'), ...p }));
  const rem = doc.removed || {};
  s.removed = {
    lines: (rem.lines || []).map((l) => ({ ...l, id: uid('l'), origin: 'engine', follows: l.follows || undefined })),
    boxes: (rem.boxes || []).map((b) => ({ ...b, id: uid('b'), origin: 'engine' })),
    marks: (rem.marks || []).map((m) => ({ ...m, id: uid('m'), origin: 'engine' })),
    parts: (rem.parts || []).map((p) => ({ ...p, id: uid('p'), origin: 'engine' })),
  };
  s.edits = { ...(doc.measurements?.person || {}) };
  s.label = { broad: doc.label?.broad || '', code: doc.label?.code || '', speciesId: doc.species?.final?.id || null, speciesLabel: doc.species?.final?.label || null };
  return s;
}
